import type { WorkExperienceCompany, WorkExperienceRole } from '@applicator/shared'
import React, { type CSSProperties } from 'react'

import { PDF_CONFIG } from '../../utils/resumeExportPdfConfig'
import {
  WORK_EXPERIENCE_TEAL_HEADER_BULLET,
  workExperienceTealLine1FontPt,
  workExperienceTealLine1HasContent,
  workExperienceTealLine1Parts,
  workExperienceTealLine2Parts,
} from '../../utils/workExperienceRoleHeaderPreview'

const TEAL_LINE1_LINE_HEIGHT = PDF_CONFIG.lineHeight.body

const TEAL_LINE2_STYLE: CSSProperties = {
  fontSize: `${PDF_CONFIG.fontSize.company}pt`,
  lineHeight: TEAL_LINE1_LINE_HEIGHT,
}

type WorkExperienceRoleTealHeaderProps = {
  company: WorkExperienceCompany
  role: WorkExperienceRole
  textClassName?: string
  /** Teal order: company line, then company description, then position line. */
  lines?: 'both' | 'company' | 'position'
}

function tealLine1PartStyle(kind: 'company' | 'text'): CSSProperties {
  return {
    fontSize: `${workExperienceTealLine1FontPt(kind)}pt`,
    lineHeight: TEAL_LINE1_LINE_HEIGHT,
  }
}

/**
 * Teal two-column role header:
 * line 1 — Company • Location • Dates (all bold; company slightly larger);
 * line 2 — Position • Employment type • Dates (position bold; rest normal).
 */
export default function WorkExperienceRoleTealHeader({
  company,
  role,
  textClassName = 'text-black',
  lines = 'both',
}: WorkExperienceRoleTealHeaderProps) {
  const line1 = workExperienceTealLine1Parts(company, role)
  const showLine1 =
    (lines === 'both' || lines === 'company') && workExperienceTealLine1HasContent(line1)

  const line2 = workExperienceTealLine2Parts(role)
  const showLine2 = (lines === 'both' || lines === 'position') && line2.length > 0

  return (
    <>
      {showLine1 && (
        <p className={`mb-0.5 font-bold ${textClassName}`}>
          {line1.map((part, index) => (
            <React.Fragment key={`${part.kind}-${index}`}>
              {index > 0 && (
                <span className="font-bold" style={tealLine1PartStyle('text')}>
                  {WORK_EXPERIENCE_TEAL_HEADER_BULLET}
                </span>
              )}
              <span className="font-bold" style={tealLine1PartStyle(part.kind)}>
                {part.text}
              </span>
            </React.Fragment>
          ))}
        </p>
      )}
      {showLine2 && (
        <p className={`mb-1 ${textClassName}`} style={TEAL_LINE2_STYLE}>
          {line2.map((part, index) => (
            <React.Fragment key={`${part.kind}-${index}`}>
              {index > 0 && (
                <span className="font-normal">{WORK_EXPERIENCE_TEAL_HEADER_BULLET}</span>
              )}
              <span className={part.kind === 'position' ? 'font-bold' : 'font-normal'}>
                {part.text}
              </span>
            </React.Fragment>
          ))}
        </p>
      )}
    </>
  )
}
